import L from "leaflet";
import type { SimState, Vehicle } from "../domain/model";
import { vehicleDefinitions } from "../domain/organizations";
import {
  compileVehicleTrack,
  continuousHeading,
  sampleVehicleTrack,
  type VehicleTrack,
} from "../providers/vehicleMotion";

type Pin = {
  marker: L.Marker;
  wrapper: HTMLDivElement;
  rotor: HTMLDivElement;
  sprite: HTMLImageElement;
  glyph: HTMLSpanElement;
  label: HTMLSpanElement;
  heading?: number;
  journeyKey?: string;
  track?: VehicleTrack;
  stationary: boolean;
};

/** Owns persistent DOM nodes; Leaflet translates the marker, only the inner body rotates. */
export class VehicleMapLayer {
  private pins = new Map<string, Pin>();
  private layer: L.LayerGroup;
  constructor(
    map: L.Map,
    private select: (vehicleId: string) => void,
  ) {
    this.layer = L.layerGroup().addTo(map);
  }
  sync(state: SimState, visible: boolean) {
    const moving = visible
      ? state.vehicles.filter(
          (v) =>
            v.journey || v.status === 4 || v.status === 8 || v.status === 1,
        )
      : [];
    const groups = new Map<string, Vehicle[]>();
    for (const v of moving) {
      if (v.journey) continue;
      const key = `${v.status}:${v.location[0]},${v.location[1]}`;
      groups.set(key, [...(groups.get(key) ?? []), v]);
    }
    const keep = new Set<string>();
    for (const v of moving) {
      const stationary = !v.journey;
      const group = stationary
        ? groups.get(`${v.status}:${v.location[0]},${v.location[1]}`)!
        : [v];
      if (stationary && group[0].id !== v.id) continue;
      keep.add(v.id);
      let pin = this.pins.get(v.id);
      if (!pin) {
        const wrapper = document.createElement("div");
        wrapper.dataset.vehicleId = v.id;
        const rotor = document.createElement("div");
        rotor.className = "vehicle-rotor";
        const sprite = document.createElement("img");
        sprite.className = "vehicle-sprite";
        sprite.alt = "";
        sprite.draggable = false;
        const arrow = document.createElement("span");
        arrow.className = "vehicle-heading-arrow";
        arrow.setAttribute("aria-hidden", "true");
        rotor.append(sprite, arrow);
        const glyph = document.createElement("span");
        glyph.className = "special-unit-glyph";
        const label = document.createElement("span");
        label.className = "vehicle-map-label";
        wrapper.append(rotor, glyph, label);
        const marker = L.marker(v.location, {
          icon: L.divIcon({
            className: "vehicle-marker",
            html: wrapper,
            iconSize: [42, 58],
            iconAnchor: [21, 29],
          }),
          zIndexOffset: 1200,
        })
          .on("click", () => this.select(v.id))
          .addTo(this.layer);
        pin = { marker, wrapper, rotor, sprite, glyph, label, stationary };
        this.pins.set(v.id, pin);
      }
      const category = vehicleDefinitions[v.kind].category;
      const symbol =
        category === "technical" || ["RTH", "ITH"].includes(v.kind);
      pin.wrapper.className = `vehicle-pin ${category} ${stationary ? "stationary" : ""} ${symbol ? "symbol-vehicle" : ""}`;
      pin.wrapper.style.transform = stationary
        ? v.status === 1
          ? "translate(30px,22px)"
          : "translate(-68px,-27px)"
        : "";
      const src = `/assets/vehicles/${category === "technical" ? "fire" : category}.png`;
      if (pin.sprite.getAttribute("src") !== src) pin.sprite.src = src;
      pin.glyph.textContent = symbol ? v.kind : "";
      pin.glyph.hidden = !symbol;
      pin.stationary = stationary;
      const journeyKey =
        v.journey?.routeStatus === "ready"
          ? `${v.journey.requestId ?? v.journey.start}:${v.journey.departureAt}:${v.journey.end}:${v.journey.geometry?.length}`
          : undefined;
      if (pin.journeyKey !== journeyKey) {
        pin.track = v.journey ? compileVehicleTrack(v.journey) : undefined;
        pin.journeyKey = journeyKey;
      }
      pin.marker.setLatLng(v.location);
      pin.label.textContent = stationary
        ? v.status === 1
          ? group.length + " einsatzbereit · wartet"
          : v.status === 8
            ? `${group.length} Kräfte · Krankenhausübergabe`
            : `${group.length} ${group.length === 1 ? "Kraft" : "Kräfte"} vor Ort`
        : `${v.name}${v.status === 7 ? " · Patiententransport" : ""}${v.journey?.routeStatus === "error" ? " · Route fehlt" : v.journey?.routeStatus === "pending" ? " · Route lädt" : v.journey?.departureAt && state.now < v.journey.departureAt ? " · rückt aus" : ""}`;
      const title = stationary
        ? group
            .map(
              (unit) =>
                unit.name +
                (unit.status === 1
                  ? " · Status 1 · wartet"
                  : unit.status === 8
                    ? " · Status 8 · Krankenhaus"
                    : ""),
            )
            .join(", ")
        : `${v.name} · Status ${v.status}`;
      const element = pin.marker.getElement();
      element?.setAttribute("title", title);
      element?.setAttribute("aria-label", title);
      // Keep the last actual heading on arrival or while a replacement route is pending.
      pin.rotor.style.transform = `rotate(${pin.heading ?? 0}deg)`;
    }
    for (const [id, pin] of this.pins)
      if (!keep.has(id)) {
        pin.marker.remove();
        this.pins.delete(id);
      }
  }
  render(now: number) {
    for (const pin of this.pins.values()) {
      if (!pin.track || pin.stationary) continue;
      const pose = sampleVehicleTrack(pin.track, now);
      pin.marker.setLatLng(pose.position);
      if (pose.heading !== undefined) {
        pin.heading = continuousHeading(pin.heading, pose.heading);
        pin.rotor.style.transform = `rotate(${pin.heading}deg)`;
        pin.wrapper.dataset.heading = String(pin.heading);
      }
      pin.wrapper.dataset.routeSegment = String(pose.segment);
    }
  }
  remove() {
    this.pins.clear();
    this.layer.remove();
  }
}
